import { ApolloProvider, useReactiveVar } from '@apollo/client';
import { setUseWhatChange } from '@simbathesailor/use-what-changed';
import Big from 'big.js';
import React, { useEffect } from 'react';
import { render } from 'react-dom';
import { Route, Router } from 'react-router-dom';
import { QueryParamProvider, transformSearchStringJsonSafe } from 'use-query-params';
import App from './App';
import PlanActivator from './components-v2/ActivatePlan/PlanActivator';
import CookieConsentBanner from './components-v2/CookieConsentBanner';
import UpgradeFlowProvider from './components-v2/DataModelV2UpgradeModal/UpgradeFlowProvider';
import { showNetworkErrorFlash } from './components-v2/Flash';
import OpenModals from './components-v2/Modal/OpenModals';
import { textVariantClassnames } from './components-v2/Text';
import { DECIMAL_PRECISION } from './constants/numbers';
import { client } from './lib/apollo';
import { onNetworkError } from './lib/apollo/clientEvents';
import Auth from './lib/Auth';
import {
  captureUtmParams,
  getAttributionProperties,
  getRudderStackPageOptions,
  setAnonIdCookie,
} from './lib/attribution';
import { GrowthBookProvider } from './lib/growthbook';
import history from './lib/history';
import { reloadOnStaleChunk } from './lib/reloadOnStaleChunk';
import AccountsProvider from './providers/AccountsProvider';
import CredentialsProvider from './providers/CredentialsProvider';
import IntegrationsProvider from './providers/IntegrationsProvider';
import PaymentProvider from './providers/PaymentProvider';
import PlansProvider from './providers/PlansProvider';
import RecalcProvider from './providers/RecalcProvider';
import TokensProvider from './providers/TokensProvider';
import TransferGroupSchemasProvider from './providers/TransferGroupSchemasProvider';
import UserProvider from './providers/UserProvider';
import VersionsProvider from './providers/VersionsProvider';
import ThemeMetaTags from './theme/ThemeMetaTags';
import ThemeApplication from './theme/ThemeApplication';
import { useTheme } from './theme/useTheme';
import { flashVar, modalsVar } from './variables';

const queryStringifyOptions = {
  transformSearchString: transformSearchStringJsonSafe,
};

setUseWhatChange(process.env.NODE_ENV === 'development');

// uncomment to use WDYR
// if (process.env.NODE_ENV === 'development') {
//   const whyDidYouRender = require('@welldone-software/why-did-you-render');
//   whyDidYouRender(React, {
//     // trackAllPureComponents: true,
//   });
// }

const refreshToken = () => Auth.refreshToken();

const Client = () => {
  const flash = useReactiveVar(flashVar);
  const { theme } = useTheme();

  useEffect(() => {
    const persistAnonId = () => setAnonIdCookie();
    if (window.rudderanalytics?.ready) {
      window.rudderanalytics.ready(persistAnonId);
    } else {
      window.setTimeout(persistAnonId, 500);
    }

    window.rudderanalytics?.page?.(
      undefined,
      undefined,
      getAttributionProperties(),
      getRudderStackPageOptions(),
    );
    const unlisten = history.listen((location) => {
      captureUtmParams();
      window.rudderanalytics?.page?.(
        undefined,
        undefined,
        { path: location.pathname, ...getAttributionProperties() },
        getRudderStackPageOptions(),
      );
    });

    return () => unlisten();
  }, []);

  useEffect(() => {
    refreshToken(); // refresh token when page is (re)-loaded

    // attempt to refresh the token on every click (the actual API call is throttled)
    document.addEventListener('mousedown', refreshToken, true);
    document.addEventListener('touchstart', refreshToken, true);

    onNetworkError((event) => {
      console.error(event); // eslint-disable-line no-console
      showNetworkErrorFlash();
    });

    client.onClearStore(async () => {
      // clear flashes and modals on logout
      modalsVar([]);
      flashVar(null);
    });
  }, []);

  return (
    <ApolloProvider client={client}>
      <Router history={history}>
        <QueryParamProvider ReactRouterRoute={Route} stringifyOptions={queryStringifyOptions}>
          <TransferGroupSchemasProvider>
            <PlansProvider>
              <UserProvider>
                <GrowthBookProvider>
                  <VersionsProvider client={client}>
                    <PaymentProvider>
                      <RecalcProvider>
                        <IntegrationsProvider>
                          <AccountsProvider>
                            <CredentialsProvider>
                              <TokensProvider>
                                <ThemeMetaTags theme={theme} />
                                <PlanActivator>
                                  <UpgradeFlowProvider>
                                    <App />
                                    {flash}
                                    <OpenModals />
                                    <CookieConsentBanner />
                                  </UpgradeFlowProvider>
                                </PlanActivator>
                              </TokensProvider>
                            </CredentialsProvider>
                          </AccountsProvider>
                        </IntegrationsProvider>
                      </RecalcProvider>
                    </PaymentProvider>
                  </VersionsProvider>
                </GrowthBookProvider>
              </UserProvider>
            </PlansProvider>
          </TransferGroupSchemasProvider>
        </QueryParamProvider>
      </Router>
    </ApolloProvider>
  );
};

Big.NE = -DECIMAL_PRECISION; // The negative exponent value at and below which toString returns exponential notation https://mikemcl.github.io/big.js/#eneg
Big.PE = DECIMAL_PRECISION; //  The positive exponent value at and above which toString returns exponential notation https://mikemcl.github.io/big.js/#epos
Big.DP = DECIMAL_PRECISION; //  The maximum number of decimal places of the results of operations involving division https://mikemcl.github.io/big.js/#dp

const root = document.getElementById('root');

// apply "base" variant styles to text by default
root?.classList.add(...textVariantClassnames.base);

// Listen before rendering, which is when lazy pages start loading. Tabs left open across a
// deploy can't load the old deploy's lazy chunks; this reloads them onto the current one.
reloadOnStaleChunk();

// Store a new click ID before rendering sends requests: the API re-issues the
// attribution cookie each request carries, which would restore the old one.
captureUtmParams();
render(
  <>
    <ThemeApplication />
    <Client />
  </>,
  root,
);
